/** IDs de video YouTube: 11 caracteres base64url. */
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const FB_SHARE_CODE = /^[A-Za-z0-9_-]{8,}$/;

/** Extrae videoId desde URL (watch, youtu.be, embed, shorts) o ID suelto. */
export function extraerVideoId(entrada) {
  const v = String(entrada || "").trim();
  if (!v) return "";
  if (VIDEO_ID.test(v)) return v;

  try {
    const url = new URL(v.startsWith("http") ? v : `https://${v}`);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0] || "";
      return VIDEO_ID.test(id) ? id : "";
    }

    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const param = url.searchParams.get("v");
      if (param && VIDEO_ID.test(param)) return param;
      const partes = url.pathname.split("/").filter(Boolean);
      const ultima = partes[partes.length - 1] || "";
      return VIDEO_ID.test(ultima) ? ultima : "";
    }
  } catch {
    return "";
  }
  return "";
}

const PLAYLIST_ID = /^[A-Za-z0-9_-]{10,64}$/;

function youtubeHost(host) {
  const h = String(host || "").replace(/^www\./, "");
  return h === "youtu.be" || h.endsWith("youtube.com") || h.endsWith("youtube-nocookie.com");
}

/** Lista de YouTube: /playlist?list= o ID tipo PL… (p. ej. PLXzt_6p0dEBY). */
export function extraerPlaylistId(entrada) {
  const v = String(entrada || "").trim();
  if (!v) return "";
  if (/^PL/i.test(v) && PLAYLIST_ID.test(v) && !VIDEO_ID.test(v)) return v;
  try {
    const url = new URL(v.startsWith("http") ? v : `https://${v}`);
    if (!youtubeHost(url.hostname)) return "";
    const list = url.searchParams.get("list") || "";
    if (!list || !PLAYLIST_ID.test(list) || VIDEO_ID.test(list)) return "";
    const partes = url.pathname.split("/").filter(Boolean);
    const esPaginaLista = partes[0] === "playlist" || (!url.searchParams.get("v") && !partes.includes("shorts") && !partes.includes("embed"));
    return esPaginaLista ? list : "";
  } catch {
    return "";
  }
}

export function esYoutubeShortsUrl(entrada) {
  const v = String(entrada || "").trim();
  if (!v) return false;
  try {
    const url = new URL(v.startsWith("http") ? v : `https://${v}`);
    if (!youtubeHost(url.hostname)) return false;
    return url.pathname.split("/").filter(Boolean)[0] === "shorts";
  } catch {
    return false;
  }
}

function facebookHost(host) {
  const h = String(host || "").replace(/^www\./, "").replace(/^m\./, "").replace(/^web\./, "");
  return h === "facebook.com" || h.endsWith(".facebook.com") || h === "fb.watch" || h === "fb.com";
}

/** Si pegan el iframe de Insertar, saca el href del plugin. */
function desenvuelvePegadoFacebook(raw) {
  let v = String(raw || "").trim();
  if (!v) return "";
  v = v.replace(/&amp;/g, "&");

  const srcMatch = v.match(/src\s*=\s*["']([^"']+)["']/i);
  if (srcMatch?.[1]) {
    try {
      const plugin = new URL(srcMatch[1]);
      const href = plugin.searchParams.get("href");
      if (href) return href;
    } catch {
      /* sigue */
    }
  }

  const hrefParam = v.match(/[?&]href=([^&\s"']+)/i);
  if (hrefParam?.[1]) {
    try {
      return decodeURIComponent(hrefParam[1]);
    } catch {
      return hrefParam[1];
    }
  }

  return v;
}

/** URL canónica de video/reel Facebook (share/v, watch, /videos/, fb.watch). */
export function extraerFacebookHref(entrada) {
  const v = desenvuelvePegadoFacebook(entrada);
  if (!v) return "";

  try {
    const url = new URL(v.startsWith("http") ? v : `https://${v}`);
    if (!facebookHost(url.hostname)) return "";

    const path = url.pathname.replace(/\/+$/, "") || "/";
    const partes = path.split("/").filter(Boolean);
    const watchV = url.searchParams.get("v") || url.searchParams.get("story_fbid");

    const esVideo =
      partes[0] === "share" && (partes[1] === "v" || partes[1] === "r") ||
      partes[0] === "watch" ||
      partes[0] === "reel" ||
      partes[0] === "reels" ||
      partes[0] === "video.php" ||
      partes.includes("videos") ||
      url.hostname.replace(/^www\./, "") === "fb.watch" ||
      Boolean(watchV);

    if (!esVideo) return "";
    if (partes[0] === "share" && partes[2] && !FB_SHARE_CODE.test(partes[2])) return "";

    const host = url.hostname.replace(/^www\./, "").replace(/^m\./, "").replace(/^web\./, "");
    if (host === "fb.watch") {
      return `https://fb.watch/${partes[0] || ""}`.replace(/\/+$/, "");
    }

    /** Reels: mismo href que el diálogo Insertar de Facebook (barra final). */
    if ((partes[0] === "reel" || partes[0] === "reels") && partes[1]) {
      const id = partes[1].replace(/\D/g, "") || partes[1];
      return `https://www.facebook.com/reel/${id}/`;
    }

    const canon = new URL("https://www.facebook.com");
    canon.pathname = path.startsWith("/") ? path : `/${path}`;
    if (canon.pathname.endsWith("/") && partes[0] !== "watch") {
      canon.pathname = canon.pathname.replace(/\/+$/, "") || "/";
    } else if (!canon.pathname.endsWith("/")) {
      if (partes[0] === "watch") canon.pathname = "/watch/";
    }
    if (watchV && !partes.includes("videos") && partes[0] === "watch") {
      canon.searchParams.set("v", watchV);
      canon.pathname = "/watch/";
    }
    return canon.toString().replace(/\/$/, partes[0] === "watch" ? "/" : "");
  } catch {
    return "";
  }
}

export function parseHeroClip(entrada) {
  const href = extraerFacebookHref(entrada);
  if (href) {
    const vertical = /facebook\.com\/reels?\//i.test(href);
    return { type: "facebook", href, id: "", playlistId: "", orientacion: vertical ? "vertical" : "horizontal" };
  }
  const playlistId = extraerPlaylistId(entrada);
  const id = extraerVideoId(entrada);
  if (playlistId && !id) {
    return { type: "youtube", id: "", href: "", playlistId, orientacion: "horizontal" };
  }
  if (id) {
    return {
      type: "youtube",
      id,
      href: "",
      playlistId: "",
      orientacion: esYoutubeShortsUrl(entrada) ? "vertical" : "horizontal",
    };
  }
  return null;
}

export function etiquetaProveedor(entrada) {
  const clip = parseHeroClip(entrada);
  if (clip?.type === "facebook") return "Facebook";
  if (clip?.type === "youtube" && clip.playlistId && !clip.id) return "YouTube lista";
  if (clip?.type === "youtube" && clip.orientacion === "vertical") return "YouTube Short";
  if (clip?.type === "youtube") return "YouTube";
  return "";
}

export function filaVideoVacia() {
  return { ref: "", proveedor: "", duracionSeg: 0, orientacion: "" };
}

function refCanonico(clip, fallback) {
  if (!clip) return String(fallback || "").trim();
  if (clip.type === "facebook") return clip.href;
  if (clip.playlistId && !clip.id) {
    return `https://www.youtube.com/playlist?list=${clip.playlistId}`;
  }
  if (clip.orientacion === "vertical" && clip.id) {
    return `https://www.youtube.com/shorts/${clip.id}`;
  }
  return clip.id;
}

function clipKeyOf(clip) {
  if (!clip) return "";
  if (clip.type === "facebook") return `fb:${clip.href}`;
  if (clip.playlistId && !clip.id) return `pl:${clip.playlistId}`;
  return `yt:${clip.id}`;
}

function filaDesdeBruto(bruto) {
  if (!bruto) return filaVideoVacia();
  if (typeof bruto === "string") {
    const clip = parseHeroClip(bruto);
    return {
      ref: refCanonico(clip, bruto),
      proveedor: clip?.type || "",
      duracionSeg: 0,
      orientacion: clip?.orientacion || "",
    };
  }
  const ref = String(bruto.ref || bruto.url || bruto.href || "").trim();
  const clip = parseHeroClip(ref);
  const proveedor = clip?.type || (bruto.proveedor === "facebook" || bruto.proveedor === "youtube" ? bruto.proveedor : "");
  const duracion = Number(bruto.duracionSeg);
  const orientacion =
    bruto.orientacion === "vertical" || bruto.orientacion === "horizontal"
      ? bruto.orientacion
      : clip?.orientacion || "";
  return {
    ref: refCanonico(clip, ref),
    proveedor,
    duracionSeg: Number.isFinite(duracion) && duracion > 0 ? Math.min(3600, Math.round(duracion)) : 0,
    orientacion,
  };
}

/** Filas de edición (incluye vacías). */
export function filasHeroVideos(src) {
  const medios = src && typeof src === "object" ? src : {};
  if (Array.isArray(medios.videos) && medios.videos.length > 0) {
    return medios.videos.map(filaDesdeBruto);
  }
  const refs = Array.isArray(medios.youtubeIds) ? medios.youtubeIds : [];
  if (!refs.length) return [filaVideoVacia()];
  return refs.map(filaDesdeBruto);
}

export function clipsDesdeFilas(filas) {
  const out = [];
  const seen = new Set();
  for (const fila of Array.isArray(filas) ? filas : []) {
    const clip = parseHeroClip(fila?.ref);
    if (!clip) continue;
    const key = clipKeyOf(clip);
    if (seen.has(key)) continue;
    seen.add(key);
    const orientacion =
      fila.orientacion === "vertical" || fila.orientacion === "horizontal"
        ? fila.orientacion
        : clip.orientacion || "horizontal";
    out.push({
      type: clip.type,
      id: clip.id || "",
      href: clip.href || "",
      playlistId: clip.playlistId || "",
      duracionSeg: Number(fila.duracionSeg) > 0 ? Number(fila.duracionSeg) : 0,
      orientacion,
    });
  }
  return out;
}

export function normalizarHeroRef(entrada) {
  const clip = parseHeroClip(entrada);
  return refCanonico(clip, entrada);
}

/** Conserva IDs YouTube y URLs Facebook (el save anterior descartaba Facebook). */
export function listaHeroRefs(rawList) {
  const out = [];
  const seen = new Set();
  for (const bruto of Array.isArray(rawList) ? rawList : []) {
    const clip = parseHeroClip(bruto);
    if (!clip) continue;
    const key = clipKeyOf(clip);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(refCanonico(clip, ""));
  }
  return out;
}

export function listaVideoIds(rawList) {
  return listaHeroRefs(rawList).filter((ref) => extraerVideoId(ref) && !extraerFacebookHref(ref));
}

function clampInt(n, min, max, fallback) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, Math.round(v)));
}

function normalizeHexColor(raw, fallback = "#ffffff") {
  const s = String(raw || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return `#${s.slice(1).toLowerCase()}`;
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function normalizeHeroMedios(medios) {
  const src = medios && typeof medios === "object" ? medios : {};
  const videos = filasHeroVideos(src);
  const clips = clipsDesdeFilas(videos);
  const youtubeIds = clips.map((c) => (c.type === "facebook" ? c.href : c.id));
  const cropTop = Number(src.cropTopPx);
  const cropBottom = Number(src.cropBottomPx);
  const titulo = String(src.tituloReproductor || "")
    .replace(/\r\n/g, "\n")
    .replace(/^\n+|\n+$/g, "");
  return {
    videos,
    youtubeIds,
    clips,
    youtubePlayIds: clips.filter((c) => c.type === "youtube").map((c) => c.id),
    facebookHrefs: clips.filter((c) => c.type === "facebook").map((c) => c.href),
    provider: clips[0]?.type || null,
    mensajeRail: String(src.mensajeRail || "").trim(),
    youtubePrivacy: src.youtubePrivacy === "public" || src.youtubePrivacy === "private"
      ? src.youtubePrivacy
      : "unlisted",
    cropTopPx: Number.isFinite(cropTop) ? Math.min(120, Math.max(0, cropTop)) : 55,
    cropBottomPx: Number.isFinite(cropBottom) ? Math.min(120, Math.max(0, cropBottom)) : 58,
    autoplayMuted: src.autoplayMuted !== false,
    enHero: src.enHero === true,
    tituloReproductor: titulo || "Nuestro contenido de YouTube",
    reproductorAncho: clampInt(src.reproductorAncho, 160, 640, 280),
    reproductorAlto: clampInt(src.reproductorAlto, 240, 960, 640),
    marcoColor: normalizeHexColor(src.marcoColor, "#ffffff"),
    marcoAncho: clampInt(src.marcoAncho, 0, 48, 10),
    desbordar: src.desbordar !== false,
  };
}
